import { PATHNAMES } from '@core/types/pathnames';

type Locale = {
  readonly router: keyof typeof PATHNAMES;
  readonly territory: string;
  readonly contentful: string;
};

/**
 * A readonly array of locale configurations for Contentful.
 * Each locale configuration includes:
 * - `router`: The language code used for routing.
 * - `territory`: The country or region code associated with the locale.
 * - `contentful`: The locale identifier used in Contentful.
 *
 * This array provides mappings between routing 'languages', territories, and Contentful locales.
 */
export const contentfulLocales: readonly Locale[] = [
  { router: 'en', territory: 'US', contentful: 'en-US' },
  { router: 'en', territory: 'GB', contentful: 'en-GB' },
  { router: 'en', territory: 'CA', contentful: 'en-CA' },
  { router: 'fr', territory: 'FR', contentful: 'fr-FR' },
  { router: 'fr', territory: 'CA', contentful: 'fr-CA' },
  { router: 'es', territory: 'ES', contentful: 'es-ES' },
  { router: 'es', territory: 'MX', contentful: 'es-MX' },
  { router: 'de', territory: 'DE', contentful: 'de-DE' },
  { router: 'de', territory: 'AT', contentful: 'de-AT' },
  { router: 'it', territory: 'IT', contentful: 'it-IT' },
  { router: 'pt', territory: 'PT', contentful: 'pt-PT' },
  { router: 'pt', territory: 'BR', contentful: 'pt-BR' },
  { router: 'nl', territory: 'NL', contentful: 'nl-NL' },
  { router: 'ru', territory: 'RU', contentful: 'ru-RU' },
  { router: 'ja', territory: 'JP', contentful: 'ja-JP' },
  { router: 'zh', territory: 'CN', contentful: 'zh-CN' },
  { router: 'zh', territory: 'TW', contentful: 'zh-TW' },
  { router: 'ko', territory: 'KR', contentful: 'ko-KR' },

  { router: 'sv', territory: 'SE', contentful: 'sv-SE' },
  { router: 'no', territory: 'NO', contentful: 'no-NO' },
  { router: 'fi', territory: 'FI', contentful: 'fi-FI' },
  { router: 'da', territory: 'DK', contentful: 'da-DK' },
  { router: 'pl', territory: 'PL', contentful: 'pl-PL' },
  { router: 'tr', territory: 'TR', contentful: 'tr-TR' },
  { router: 'cs', territory: 'CZ', contentful: 'cs-CZ' },
  { router: 'hu', territory: 'HU', contentful: 'hu-HU' },
  { router: 'sk', territory: 'SK', contentful: 'sk-SK' },
  { router: 'ro', territory: 'RO', contentful: 'ro-RO' },
  { router: 'el', territory: 'GR', contentful: 'el-GR' },
  { router: 'hr', territory: 'HR', contentful: 'hr-HR' },
  { router: 'bg', territory: 'BG', contentful: 'bg-BG' },
  { router: 'uk', territory: 'UA', contentful: 'uk-UA' },
  { router: 'th', territory: 'TH', contentful: 'th-TH' },
  { router: 'vi', territory: 'VN', contentful: 'vi-VN' },
  { router: 'id', territory: 'ID', contentful: 'id-ID' },
  { router: 'ms', territory: 'MY', contentful: 'ms-MY' },
  { router: 'ml', territory: 'IN', contentful: 'ml-IN' },
  { router: 'ta', territory: 'IN', contentful: 'ta-IN' },
  { router: 'hi', territory: 'IN', contentful: 'hi-IN' },
  { router: 'bn', territory: 'IN', contentful: 'bn-IN' },
  { router: 'ne', territory: 'NP', contentful: 'ne-NP' },
  { router: 'si', territory: 'LK', contentful: 'si-LK' },
  { router: 'km', territory: 'KH', contentful: 'km-KH' },
  { router: 'lo', territory: 'LA', contentful: 'lo-LA' },
  { router: 'my', territory: 'MM', contentful: 'my-MM' },
  { router: 'mr', territory: 'IN', contentful: 'mr-IN' },
  { router: 'pa', territory: 'IN', contentful: 'pa-IN' },
  { router: 'gu', territory: 'IN', contentful: 'gu-IN' },
  { router: 'or', territory: 'IN', contentful: 'or-IN' },
  { router: 'te', territory: 'IN', contentful: 'te-IN' },
  { router: 'kn', territory: 'IN', contentful: 'kn-IN' },
  { router: 'as', territory: 'IN', contentful: 'as-IN' },
  { router: 'cy', territory: 'GB', contentful: 'cy-GB' },
  { router: 'sq', territory: 'AL', contentful: 'sq-AL' },
  { router: 'he', territory: 'IL', contentful: 'he-IL' },
  { router: 'en', territory: 'NZ', contentful: 'en-NZ' },
  { router: 'en', territory: 'ZA', contentful: 'en-ZA' },
  { router: 'en', territory: 'PH', contentful: 'en-PH' },
  { router: 'pt', territory: 'MO', contentful: 'pt-MO' },
  { router: 'fr', territory: 'BE', contentful: 'fr-BE' },
  { router: 'it', territory: 'CH', contentful: 'it-CH' },
  { router: 'de', territory: 'CH', contentful: 'de-CH' },
  { router: 'es', territory: 'GT', contentful: 'es-GT' },
  { router: 'fr', territory: 'LU', contentful: 'fr-LU' },
  { router: 'en', territory: 'IE', contentful: 'en-IE' },
  { router: 'en', territory: 'IN', contentful: 'en-IN' },
  { router: 'en', territory: 'SG', contentful: 'en-SG' },
];

/**
 * Retrieves a list of Contentful locales that match the specified codes.
 *
 * @param codes - An array of locale codes to filter by.
 * @returns An array of `Locale` objects whose `contentful` property matches any of the provided codes.
 */
export const getContentfulLocalesByCodes = (
  codes: readonly string[]
): readonly Locale[] => {
  const set = new Set(codes);
  return contentfulLocales.filter(({ contentful }) => set.has(contentful));
};
